import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { TABLES } from '../../../../../Apis/Tables';
import {useParamsContextProvider} from '../../../../../hooks/ParamsContextProvider';
import {SUB_VIEW_MODEL, TABS_FOODIE} from '../../Model/Models';
import {DeleteItemsApi} from '../../../../../reduxToolkit/Commons/CommonsApi';
import {useTranslation} from 'react-i18next';
import {permissionObj} from '../../../../../Helpers/permission';
import {CancelAccessCode} from "../../../../../reduxToolkit/FoodieAccessCode/FoodieAccessCodeApi";
import { errorMsg } from '../../../../../Apis/functions';

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 */
const ScheduleApp = ({
     setSubView,
     setModified,
     option,
     setOption
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { paramsContext, setParamsContext } = useParamsContextProvider();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [selectForDelete, setSelectForDelete] = useState([]);
    const [selectForCancel, setSelectForCancel] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [isChecked, setIsChecked] = useState(0);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [cancelModalShow, setCancelModalShow] = useState(false);

    useEffect(() => {
        if(DEV || permission?.includes(permissionObj?.WEB_FOODIE_SHIFT_READ)){
            setOption(TABLES.FOODIE_SHIFT);
            setSubView(SUB_VIEW_MODEL.SCHEDULE.SHIFTS);
            return;
        }

        if(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MEAL_TIME_READ)){
            setOption(TABLES.FOODIE_TYPE);
            setSubView(SUB_VIEW_MODEL.SCHEDULE.MEALS)
            return;
        }

        if(DEV || permission?.includes(permissionObj?.WEB_FOODIE_ACCESS_READ)){
            setOption(TABLES.FOODIE_ACCESS);
            setSubView(SUB_VIEW_MODEL.SCHEDULE.ACCESS_CODE)
        }
    }, [paramsContext?.tabIndex]);

    /**
     * Enable/disable the delete button from context and/or globally.
     */
    useEffect(() => {
        if(paramsContext && paramsContext?.tabIndex == TABS_FOODIE.SCHEDULES.INDEX){
            setParamsContext(prev => ({
                ...prev,
                disabledBtnDelete: selectForDelete?.length <= 0
            }));
        }
    }, [selectForDelete]);

    /**
     * useEffect to open the delete modal based on the modalDelete variable from context and selectForDelete.
     */
    useEffect(() => {
        if(paramsContext && paramsContext?.tabIndex == TABS_FOODIE.SCHEDULES.INDEX){
            // Closing is unconditional: the modal clears the selection itself, so the length check only applies when opening
            if(!paramsContext?.modalDelete){
                setDeleteModalShow(false);
            } else if(selectForDelete?.length > 0){
                setDeleteModalShow(true);
                setIsChecked(prev => prev + 1);
            }
        }

    }, [paramsContext?.modalDelete]);

    /**
     * useEffect to clear the checkbox selection when the delete modal is canceled.
     */
    useEffect(() => {
        if(paramsContext && paramsContext?.tabIndex == TABS_FOODIE.SCHEDULES.INDEX){
            if(!paramsContext?.modalDelete && isChecked > 0) {
                setSelectForDelete([]);
                setIsAllChecked(false);
                setIsChecked(0)
            }
        }
    }, [paramsContext?.modalDelete]);

    /**
     * Opens the cancel modal when `modalCancel` from `paramsContext` is true
     * and there are items selected in `selectForDelete`. Also increments a counter
     * to track that the modal was opened.
     */
    useEffect(() => {
        if (paramsContext && paramsContext?.tabIndex === TABS_FOODIE.SCHEDULES.INDEX && selectForCancel?.length > 0) {
            setCancelModalShow(paramsContext.modalCancel);
            setIsChecked(prev => prev + 1);
        }
    }, [paramsContext?.modalCancel]);

    /**
     * Clears the checkbox selection when the cancel modal is closed.
     * Ensures that `selectForDelete` is emptied and the counter reset after
     * the modal is dismissed.
     */
    useEffect(() => {
        if (paramsContext && paramsContext?.tabIndex === TABS_FOODIE.SCHEDULES.INDEX) {
            if (!paramsContext?.modalCancel && isChecked > 0) {
                setSelectForDelete([]);
                setIsAllChecked(false);
                setIsChecked(0);
            }
        }
    }, [paramsContext?.modalCancel]);


    /**
     * Function to hide the delete modal.
     */
    const handleDeleteModalClose = () => {
        if(setParamsContext){
            setParamsContext(prev => ({
                ...prev,
                modalDelete: false
            }));
        }
    }

    /**
     * Function to hide the cancel modal.
     */
    const handleCancelModalClose = () => {
        if(setParamsContext){
            setParamsContext(prev => ({
                ...prev,
                modalCancel: false
            }));
        }
    }
    /**
     * Function for the meals button.
     */
    const handleBtnMeals = () => {
        if(selectForDelete?.length > 0){
            setSelectForDelete([]);
        }
        setIsAllChecked(false);
        setOption(TABLES.FOODIE_TYPE);
        setSubView(SUB_VIEW_MODEL.SCHEDULE.MEALS)
    }

    /**
     * Function for the shifts button.
     */
    const handleBtnShifts = () => {
        if(selectForDelete?.length > 0){
            setSelectForDelete([]);
        }
        setIsAllChecked(false);
        setOption(TABLES.FOODIE_SHIFT);
        setSubView(SUB_VIEW_MODEL.SCHEDULE.SHIFTS)
    }

    /**
     * Handles the "Access Code" button click.
     *
     * Clears any previously selected items in `selectForDelete` to ensure
     * a fresh selection and switches the current subview to the Access Code view.
     */
    const handleBtnAccessCode = () => {
        if(selectForDelete?.length > 0){
            setSelectForDelete([]);
        }
        setIsAllChecked(false);
        setOption(TABLES.FOODIE_ACCESS);
        setSubView(SUB_VIEW_MODEL.SCHEDULE.ACCESS_CODE)
    }

    /**
     * Function that calls the API to delete selected items or to delete a single item.
     */
    const handleDeleteSelected = () => {
        if (selectForDelete.length > 0) {
            dispatch(DeleteItemsApi({ tableName: option , body: selectForDelete }))
                .then(({payload: {data: { success, code }}}) => {
                if (success === true) {
                    setModified(true);
                    setSelectForDelete([]);
                    setIsAllChecked(false);
                    setIsChecked(0)
                    handleDeleteModalClose();
                }else errorMsg(code);
            });
        }
    };

    /**
     * Cancels the selected access code(s) by calling the API with a provided description.
     *
     * This function works only if there are items selected in `selectForDelete`.
     * It dispatches the `CancelAccessCode` action with the first selected ID and
     * the provided cancellation description. Upon successful cancellation, it:
     *   - Increments the `modified` counter to trigger re-renders or data refresh.
     *   - Clears the `selectForDelete` array.
     *   - Resets the `isChecked` counter.
     *   - Closes the cancel modal via `handleCancelModalClose`.
     *
     * @param {string} description - The reason or description for canceling the access code.
     */
    const handleCancelSelected = (description) => {
        if (selectForCancel.length > 0) {
            dispatch(CancelAccessCode({ id: selectForCancel[0], cancelDescription: description })).then(({payload: {data: { success, code }}}) => {
                if (success === true) {
                    setModified(true);
                    setSelectForDelete([]);
                    setIsAllChecked(false);
                    setIsChecked(0)
                    handleCancelModalClose();
                }else{
                    errorMsg(code);
                }
            });
        }
    };

    return {
        t,
        DEV,
        permission,
        deleteModalShow,
        selectForDelete,
        setSelectForDelete,
        selectForCancel,
        setSelectForCancel,
        isAllChecked,
        setIsAllChecked,
        cancelModalShow,
        handleDeleteSelected,
        handleCancelSelected,
        handleBtnMeals,
        handleBtnShifts,
        handleDeleteModalClose,
        handleCancelModalClose,
        handleBtnAccessCode
    }

}

export default ScheduleApp;
